import React, {useEffect, useRef} from "react"
import {useClickAway, useToggle} from "react-use"
import classNames from "classnames"
import Icon from "../Icon/Icon"
import {IcomoonIconName} from "../Icon/icons"
import BodyPortal from "../BodyPortal/BodyPortal"

export interface ActionButtonProps {
	enableActions: boolean
	children: React.ReactNode
}

export interface ActionItemProps {
	onClick: () => void
	title: string
	icon: IcomoonIconName
	color?: string
	withTopBorder?: boolean
	disabled?: boolean
	stopPropagationOnClick?: boolean
	props?: React.HTMLAttributes<any>
}

export function ActionButton({enableActions, children}: ActionButtonProps) {
	const ref = useRef<HTMLDivElement>(null)
	const dropdownMenuRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLDivElement>(null)
	const [isActive, toggleIsActive] = useToggle(false)
	useClickAway(dropdownMenuRef, () => toggleIsActive(false))

	useEffect(() => {
		const dropdownMenu = dropdownMenuRef.current
		const trigger = triggerRef.current
		if (ref.current && dropdownMenu && trigger) {
			const rect = ref.current.getBoundingClientRect()
			dropdownMenu.style.top = `${rect.top + trigger.offsetHeight}px`
			dropdownMenu.style.left = `${rect.right - dropdownMenu.offsetWidth - trigger.offsetWidth}px`
			dropdownMenu.style.bottom = "initial"
			dropdownMenu.style.right = "initial"
		}
	}, [isActive])

	const actionButtonClassnames = classNames(
		"action-buttons-dots dropdown is-right is-vertical-middle custom",
		isActive && "is-active",
		!enableActions && "action-disabled"
	)

	return (
		<div
			ref={ref}
			className={actionButtonClassnames}
			onClick={(e) => {
				if (enableActions) {
					toggleIsActive()
				}
				e.preventDefault()
				e.stopPropagation()
			}}
		>
			<div ref={triggerRef} className="dropdown-trigger">
				<Icon icon={"navigation-menu-vertical--interface-essential"} size={16} />
			</div>
			<BodyPortal>
				<div className={actionButtonClassnames}>
					<div
						ref={dropdownMenuRef}
						className="dropdown-menu"
						style={{position: "fixed", top: "0", left: "0"}}
						role="menu"
					>
						<div className="dropdown-content">{children}</div>
					</div>
				</div>
			</BodyPortal>
		</div>
	)
}

export function ActionItem({
	onClick,
	icon,
	title,
	color,
	withTopBorder,
	disabled,
	stopPropagationOnClick,
	...props
}: ActionItemProps) {
	return (
		<a
			{...props}
			className={classNames(
				"dropdown-item",
				"action-buttons-action-item",
				withTopBorder && "with-top-border",
				disabled && "dropdown-item-disabled"
			)}
			style={{color: color}}
			onClick={(e) => {
				if (!disabled) {
					e.preventDefault()
					if (stopPropagationOnClick ?? true) {
						e.stopPropagation()
					}
					onClick()
				}
			}}
		>
			<div className="is-flex is-align-items-center">
				<Icon icon={icon} size={16} />
				<span>{title}</span>
			</div>
		</a>
	)
}
